<header-row
   [userName]="'Victoria Brown'"
   [userRole]="'REGIONAL MANAGER'"></header-row>

<div class="page-layout search-results-page">
   <!-- Title only — search bar lives in the header (single global bar). -->
   <div class="search-results-header">
      <h2 class="main-title">Search Results</h2>
   </div>

   <!-- Filter tabs -->
   <div
      class="search-filter-tabs"
      *ngIf="query">
      <button
         type="button"
         *ngFor="let tab of filterTabs"
         class="filter-tab"
         [class.active]="filter === tab.key"
         (click)="onFilterChange(tab.key)">
         {{ tab.label }}
      </button>
   </div>

   <!-- Summary line -->
   <div
      class="search-summary"
      *ngIf="query && !isLoading && !hasError">
      <span>
         Search results for <strong>"{{ query }}"</strong> —
         <strong>{{ visibleTotal }}</strong> result{{ visibleTotal === 1 ? '' : 's' }}
      </span>
   </div>

   <!-- Loading -->
   <div
      class="search-state-card"
      *ngIf="isLoading">
      <mat-spinner diameter="32"></mat-spinner>
      <span>Searching for "{{ query }}"...</span>
   </div>

   <!-- Error -->
   <div
      class="search-state-card error"
      *ngIf="hasError && !isLoading">
      <i class="pi pi-exclamation-circle"></i>
      <span>We couldn't load search results. Please try again.</span>
      <button
         type="button"
         class="retry-btn"
         (click)="retry()">
         Retry
      </button>
   </div>

   <!-- No query yet (landed without ?q=) -->
   <div
      class="search-state-card empty"
      *ngIf="!query && !isLoading && !hasError">
      <i class="pi pi-search"></i>
      <span>Type a keyword in the search bar to see results.</span>
   </div>

   <!-- Query present but below the min-char threshold — no API was called. -->
   <div
      class="search-state-card empty"
      *ngIf="query && query.length < minSearchChars && !isLoading && !hasError">
      <i class="pi pi-search"></i>
      <span>Keep typing — at least {{ minSearchChars }} characters needed.</span>
   </div>

   <!-- Page-level empty: query has hits zero results -->
   <div
      class="search-state-card empty"
      *ngIf="query && query.length >= minSearchChars && !isLoading && !hasError && totalResults === 0">
      <i class="pi pi-inbox"></i>
      <span>No results found for <strong>"{{ query }}"</strong>. Try a different keyword.</span>
   </div>

   <!-- Result groups -->
   <ng-container *ngIf="query && query.length >= minSearchChars && !isLoading && !hasError && totalResults > 0">
      <!-- Hospitals -->
      <section
         class="result-group-card"
         *ngIf="isFilterVisible('hospitals')">
         <div class="group-header">
            <h3>
               <i class="pi pi-building group-icon hospital"></i>
               Hospitals
               <span class="group-count">({{ results.hospitals.length }})</span>
            </h3>
         </div>
         <div
            class="group-empty"
            *ngIf="results.hospitals.length === 0">
            No Hospitals matched "{{ query }}".
         </div>
         <ul
            class="result-list"
            *ngIf="results.hospitals.length > 0">
            <li
               class="result-row"
               *ngFor="let item of results.hospitals; trackBy: trackById"
               (click)="onResultClick(item)">
               <div class="row-icon hospital"><i class="pi pi-building"></i></div>
               <div class="row-body">
                  <div class="row-title">{{ item.title || '(Untitled hospital)' }}</div>
                  <div
                     class="row-snippet"
                     *ngIf="item.snippet"
                     [innerHTML]="item.snippet"></div>
               </div>
               <div
                  class="row-date"
                  *ngIf="item.date">
                  {{ item.date | date: 'MMM d, y' }}
               </div>
            </li>
         </ul>
      </section>

      <!-- Goals -->
      <section
         class="result-group-card"
         *ngIf="isFilterVisible('goals')">
         <div class="group-header">
            <h3>
               <i class="pi pi-bullseye group-icon goal"></i>
               Goals
               <span class="group-count">({{ results.goals.length }})</span>
            </h3>
         </div>
         <div
            class="group-empty"
            *ngIf="results.goals.length === 0">
            No Goals matched "{{ query }}".
         </div>
         <ul
            class="result-list"
            *ngIf="results.goals.length > 0">
            <li
               class="result-row"
               *ngFor="let item of results.goals; trackBy: trackById"
               (click)="onResultClick(item)">
               <div class="row-icon goal"><i class="pi pi-bullseye"></i></div>
               <div class="row-body">
                  <div class="row-title">{{ item.title || '(Untitled goal)' }}</div>
                  <div
                     class="row-snippet"
                     *ngIf="item.snippet"
                     [innerHTML]="item.snippet"></div>
               </div>
               <div
                  class="row-date"
                  *ngIf="item.date">
                  {{ item.date | date: 'MMM d, y' }}
               </div>
            </li>
         </ul>
      </section>

      <!-- Tasks -->
      <section
         class="result-group-card"
         *ngIf="isFilterVisible('tasks')">
         <div class="group-header">
            <h3>
               <i class="pi pi-check-square group-icon task"></i>
               Tasks
               <span class="group-count">({{ results.tasks.length }})</span>
            </h3>
         </div>
         <div
            class="group-empty"
            *ngIf="results.tasks.length === 0">
            No Tasks matched "{{ query }}".
         </div>
         <ul
            class="result-list"
            *ngIf="results.tasks.length > 0">
            <li
               class="result-row"
               *ngFor="let item of results.tasks; trackBy: trackById"
               (click)="onResultClick(item)">
               <div class="row-icon task"><i class="pi pi-check-square"></i></div>
               <div class="row-body">
                  <div class="row-title">{{ item.title || '(Untitled task)' }}</div>
                  <div
                     class="row-snippet"
                     *ngIf="item.snippet"
                     [innerHTML]="item.snippet"></div>
               </div>
               <div
                  class="row-date"
                  *ngIf="item.date">
                  {{ item.date | date: 'MMM d, y' }}
               </div>
            </li>
         </ul>
      </section>

      <!-- Meetings -->
      <section
         class="result-group-card"
         *ngIf="isFilterVisible('meetings')">
         <div class="group-header">
            <h3>
               <i class="pi pi-calendar group-icon meeting"></i>
               Meetings
               <span class="group-count">({{ results.meetings.length }})</span>
            </h3>
         </div>
         <div
            class="group-empty"
            *ngIf="results.meetings.length === 0">
            No Meetings matched "{{ query }}".
         </div>
         <ul
            class="result-list"
            *ngIf="results.meetings.length > 0">
            <li
               class="result-row"
               *ngFor="let item of results.meetings; trackBy: trackById"
               (click)="onResultClick(item)">
               <div class="row-icon meeting"><i class="pi pi-calendar"></i></div>
               <div class="row-body">
                  <div class="row-title">{{ item.title || '(Untitled meeting)' }}</div>
                  <div
                     class="row-snippet"
                     *ngIf="item.snippet"
                     [innerHTML]="item.snippet"></div>
               </div>
               <div
                  class="row-date"
                  *ngIf="item.date">
                  {{ item.date | date: 'MMM d, y' }}
               </div>
            </li>
         </ul>
      </section>

      <!-- Pagination (single, global) -->
      <div
         class="search-pagination"
         *ngIf="totalPages > 1">
         <button
            type="button"
            class="pager-btn"
            [disabled]="!hasPrevious"
            (click)="prevPage()">
            <i class="pi pi-chevron-left"></i>
            Previous
         </button>
         <span class="pager-info">Page {{ page }} of {{ totalPages }}</span>
         <button
            type="button"
            class="pager-btn"
            [disabled]="!hasNext"
            (click)="nextPage()">
            Next
            <i class="pi pi-chevron-right"></i>
         </button>
      </div>
   </ng-container>
</div>
